import "./appVersions.scss";
import { useEffect, useRef, useState } from "react";
import { Ring } from "@uiball/loaders";
import { Toast } from "primereact/toast";
import { useGetAppVersions, useSaveAppVersion } from "hooks/useAppVersions";

/**
 * App-version (force-update) management section for the settings page.
 *
 * Renders one card per mobile app. Each card has an iOS and an Android column,
 * and each column edits the minimum required version + store download link.
 * Saving a card writes both platforms for that app via PUT /api/app-versions/:app.
 * Reading is via the public GET /api/app-versions endpoint the apps also use.
 */

const APP_META = [
    { key: "customer", title: "تطبيق الزبون", subtitle: "إصدارات تطبيق الزبائن." },
    { key: "vendor", title: "تطبيق المطعم", subtitle: "إصدارات تطبيق المطاعم." },
    { key: "driver", title: "تطبيق السائق", subtitle: "إصدارات تطبيق السائقين." },
    // The admin app, com.talabatek.adminapp (talabatk/api#224).
    { key: "admin", title: "تطبيق الإدارة", subtitle: "إصدارات تطبيق المشرفين." },
];

// Android first so it renders on the right in the RTL layout, matching the
// store-link placeholders the merchants are used to seeing.
const PLATFORM_META = [
    { key: "android", label: "Android", icon: "🤖" },
    { key: "ios", label: "iOS", icon: "🍎" },
];

/** Build editable form state from a server-provided app config (or empties). */
const buildForm = (data) => ({
    android: {
        minVersion: data?.android?.minVersion ?? "",
        downloadUrl: data?.android?.downloadUrl ?? "",
    },
    ios: {
        minVersion: data?.ios?.minVersion ?? "",
        downloadUrl: data?.ios?.downloadUrl ?? "",
    },
});

const AppVersionCard = ({ meta, data, showMessage }) => {
    const saveMutation = useSaveAppVersion();
    const [form, setForm] = useState(() => buildForm(data));

    // Re-sync local form whenever the server data changes (initial load,
    // refetch after save). The reset button restores to this same baseline.
    useEffect(() => {
        setForm(buildForm(data));
    }, [data]);

    const handleChange = (platform, field, value) => {
        setForm((prev) => ({
            ...prev,
            [platform]: { ...prev[platform], [field]: value },
        }));
    };

    const handleReset = () => setForm(buildForm(data));

    const handleSave = async () => {
        for (const platform of PLATFORM_META) {
            if (!`${form[platform.key].minVersion}`.trim()) {
                showMessage(
                    "warn",
                    "تنبيه",
                    `الرجاء إدخال الحد الأدنى للإصدار (${platform.label})`,
                );
                return;
            }
        }

        try {
            await saveMutation.mutateAsync({ app: meta.key, versions: form });
            showMessage("success", "تم الحفظ", "تم حفظ إعدادات الإصدار بنجاح");
        } catch (err) {
            showMessage("error", "خطأ", err.message || "تعذر حفظ الإعدادات");
        }
    };

    return (
        <div className="version-card">
            <div className="version-card__head">
                <div className="version-card__titles">
                    <h4>{meta.title}</h4>
                    <p>{meta.subtitle}</p>
                </div>
                <div className="version-card__actions">
                    <button
                        className="btn-save"
                        type="button"
                        onClick={handleSave}
                        disabled={saveMutation.isPending}
                    >
                        {saveMutation.isPending ? "جارٍ الحفظ..." : "💾 حفظ"}
                    </button>
                    <button className="btn-reset" type="button" onClick={handleReset}>
                        ↺ إعادة ضبط
                    </button>
                </div>
            </div>

            <div className="version-card__platforms">
                {PLATFORM_META.map((platform) => (
                    <div className="platform-col" key={platform.key}>
                        <div className="platform-col__head">
                            <span>{platform.label}</span>
                            <span className="platform-col__icon">{platform.icon}</span>
                        </div>

                        <label>الحد الأدنى للإصدار</label>
                        <input
                            className="form-control"
                            type="text"
                            value={form[platform.key].minVersion}
                            onChange={(e) =>
                                handleChange(platform.key, "minVersion", e.target.value)
                            }
                        />

                        <label>🔗 رابط التحميل</label>
                        <input
                            className="form-control"
                            type="text"
                            dir="ltr"
                            placeholder="https://play.google.com/..."
                            value={form[platform.key].downloadUrl}
                            onChange={(e) =>
                                handleChange(platform.key, "downloadUrl", e.target.value)
                            }
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

const AppVersions = () => {
    const { appVersions, loading } = useGetAppVersions();
    const toast = useRef(null);

    const showMessage = (severity, summary, detail) => {
        toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    return (
        <div className="app-versions">
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />

            {/* One card for the section, heading inside, matching every other
                card on the page. The per-app blocks below are separated by a
                rule rather than being cards inside a card. */}
            <h3>التحكم في إصدار التطبيقات</h3>

            {loading ? (
                <div className="loadingSpinner center">
                    <Ring size={40} lineWeight={5} speed={2} color="#f58220" />
                </div>
            ) : (
                APP_META.map((meta) => (
                    <AppVersionCard
                        key={meta.key}
                        meta={meta}
                        data={appVersions?.[meta.key]}
                        showMessage={showMessage}
                    />
                ))
            )}
        </div>
    );
};

export default AppVersions;
